'use client'

import { parseColor } from '@ark-ui/react'
import { LineFrame } from '@qr-front/icons/line-frame'
import { Checkbox } from '@qr-front/ui/components/Checkbox'
import { ColorPicker } from '@qr-front/ui/components/ColorPicker'
import { InputField } from '@qr-front/ui/components/InputField'
import { SmartScrollContainer } from '@qr-front/ui/components/SmartScrollContainer'
import { cn, isColorWhite } from '@qr-front/ui/utils'
import * as RadioGroup from '@radix-ui/react-radio-group'
import { FRAME_TEXT_MAX_LENGTH } from 'constants/validationConstants'
import type { TFrameType } from 'features/generator/services/types'
import { useAtom, useAtomValue } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { PrimaryAndSecondaryColorFields } from '@/components/PrimaryAndSecondaryColorFields'
import { useFrameFontSize } from '@/generator/client'
import { getStore } from '@/store'
import { maxLength, removeEmojis } from '@/utils'

import { getColorPickerTranslations } from '../../../../utils/getColorPickerTranslations'
import { captureBuilderEvent } from '../../builderAnalytics'
import { TGeneratorFormSection } from '../../components/GeneratorFormSection'
import { TFrame } from '../../qrFrames'
import { FRAME_TYPE_OPTION_LIST } from '../../qrFrames'
import {
	FRAME_BLACK,
	TRANSPARENT_COLOR,
} from '../../qrFrames/qrFramesConstants'
import { NoneIcon } from './NoneIcon'
import { qrBackgroundColorAtom } from './PatternDesignForm'
import { qrStyleOptionCls } from './QrStyleField'

const DEFAULT_FRAME_BACKGROUND_COLOR = '#FFFFFF'

export const frameTypeAtom = atomWithReset<TFrameType>('default')
export const frameTextAtom = atomWithReset('')
export const frameColorAtom = atomWithReset(FRAME_BLACK)
export const frameBackgroundColorAtom = atomWithReset(
	DEFAULT_FRAME_BACKGROUND_COLOR,
)
export const frameTextColorAtom = atomWithReset('')

const frameOptionCls = cn(qrStyleOptionCls, 'h-[100px] w-20 [&>svg]:w-[64px]')

const FrameOption = ({
	value,
	Frame,
	customMaxWidth,
	customMaxFontSize,
	customFontFamily,
}: TFrame) => {
	const t = useTranslations()

	const previewText = t(
		'generator__design_form__frame_section__frame_text_placeholder',
	)

	const fontSize = useFrameFontSize({
		text: previewText,
		maxWidth: customMaxWidth,
		maxSize: customMaxFontSize,
		fontFamily: customFontFamily,
	})

	return (
		<RadioGroup.Item
			key={value}
			value={value === undefined ? 'none' : value}
			className={frameOptionCls}
		>
			<Frame
				key={value}
				previewMode
				text={previewText}
				fontSize={fontSize}
			/>
		</RadioGroup.Item>
	)
}

const FrameTypeField = () => {
	const t = useTranslations()
	const [selectedType, setSelectedType] = useAtom(frameTypeAtom)

	const handleValueChange = (value: string) => {
		captureBuilderEvent('builder_select_design', {
			design_scope: 'qr_code',
			design_element: 'frame',
			design_value: value,
			option_position: FRAME_TYPE_OPTION_LIST.findIndex(
				(item) => item.value === value,
			),
			is_default: value === 'default',
			section: 'wizard_content',
		})
		setSelectedType(value as TFrameType)
		if (value !== 'default') {
			const currentFrame = FRAME_TYPE_OPTION_LIST.find(
				(item) => item.value === value,
			)
			const shouldEnableQrBackground =
				!getStore().get(qrBackgroundColorAtom) &&
				currentFrame?.incompatibleWithTransparentQrBackground
			if (shouldEnableQrBackground) {
				getStore().set(qrBackgroundColorAtom, '#FFFFFF')
			}
			const textColor = getStore().get(frameTextColorAtom)
			const shouldResetTextColor =
				!textColor ||
				textColor === FRAME_BLACK ||
				isColorWhite(parseColor)(textColor)
			if (shouldResetTextColor) {
				const shouldSetTextColorToBlack =
					currentFrame?.darkDefaultTextColor
				getStore().set(
					frameTextColorAtom,
					shouldSetTextColorToBlack ? FRAME_BLACK : '#FFFFFF',
				)
			}
		}
	}

	return (
		<SmartScrollContainer
			onFirstScroll={() =>
				captureBuilderEvent('builder_scroll_options', {
					design_element: 'frame',
				})
			}
		>
			<RadioGroup.Root
				value={selectedType}
				onValueChange={handleValueChange}
				className={cn('grid grid-flow-row grid-rows-2 gap-2')}
				style={{
					gridTemplateColumns: `repeat(${Math.ceil(FRAME_TYPE_OPTION_LIST.length / 2)}, max-content)`,
				}}
			>
				<RadioGroup.Item
					value="default"
					className={cn(
						frameOptionCls,
						'flex-col gap-2 [&>svg]:size-6',
					)}
				>
					<NoneIcon />
					<span className="text-10 font-[700] leading-[10px] text-black">
						{t(
							'generator__design_form__frame_section__no_frame_label',
						)}
					</span>
				</RadioGroup.Item>
				{FRAME_TYPE_OPTION_LIST.map(FrameOption)}
			</RadioGroup.Root>
		</SmartScrollContainer>
	)
}

const TextField = () => {
	const t = useTranslations()
	const [frameText, setFrameText] = useAtom(frameTextAtom)

	const defaultFrameText = t(
		'generator__design_form__frame_section__frame_text_placeholder',
	)

	useEffect(() => {
		if (!getStore().get(frameTextAtom)) {
			setFrameText(defaultFrameText)
		}
	}, [defaultFrameText, setFrameText])

	return (
		<InputField
			type="text"
			value={frameText}
			onChange={(event) => {
				event.target.value = removeEmojis(event.target.value)
				const adaptedEvent = maxLength(FRAME_TEXT_MAX_LENGTH)(event)
				setFrameText(adaptedEvent.target.value)
			}}
			label={t('generator__design_form__frame_section__frame_text_label')}
			className="flex-shrink grow [flex-basis:0]"
		/>
	)
}

const FrameColorField = () => {
	const t = useTranslations()

	const [frameColor, setFrameColor] = useAtom(frameColorAtom)
	const frameType = useAtomValue(frameTypeAtom)
	const shouldDisableFrameColor = FRAME_TYPE_OPTION_LIST.find(
		({ value }) => value === frameType,
	)?.noFrameColor

	return (
		<ColorPicker
			disabled={!!shouldDisableFrameColor}
			value={frameColor}
			onValueChange={(value) => setFrameColor(value)}
			setValue={(value) => setFrameColor(value)}
			label={t(
				'generator__design_form__frame_section__frame_color_label',
			)}
			className="flex-shrink grow [flex-basis:0]"
			translations={getColorPickerTranslations(t)}
		/>
	)
}

export const FrameDesignForm = () => {
	const t = useTranslations()

	const [backgroundColor, setBackgroundColor] = useAtom(
		frameBackgroundColorAtom,
	)
	const isBackgroundTransparent = backgroundColor === TRANSPARENT_COLOR
	const [textColor, setTextColor] = useAtom(frameTextColorAtom)

	const [savedBackgroundColor, setSavedBackgroundColor] = useState(() => {
		const initialBackgroundColor = getStore().get(frameBackgroundColorAtom)
		return initialBackgroundColor === TRANSPARENT_COLOR
			? DEFAULT_FRAME_BACKGROUND_COLOR
			: initialBackgroundColor
	})

	const frameType = useAtomValue(frameTypeAtom)
	const hasFrame = frameType !== 'default'
	const disableFrameBackground = FRAME_TYPE_OPTION_LIST.find(
		({ value }) => value === frameType,
	)?.noFrameBackground

	return (
		<div className="flex max-w-full flex-col gap-6 tablet:gap-8 desktop:max-w-[840px]">
			<FrameTypeField />
			{hasFrame ? (
				<div className="rounded-10 bg-light-grey-70 p-4 tablet:p-6">
					<div className="mb-4 flex w-full flex-col gap-4 tablet:flex-row tablet:gap-18">
						<TextField />
						<FrameColorField />
					</div>
					<PrimaryAndSecondaryColorFields
						primaryColor={backgroundColor}
						setPrimaryColor={setBackgroundColor}
						primaryLabel={t(
							'generator__design_form__frame_section__background_color_label',
						)}
						isPrimaryDisabled={
							isBackgroundTransparent || !!disableFrameBackground
						}
						secondaryColor={textColor}
						setSecondaryColor={setTextColor}
						secondaryLabel={t(
							'generator__design_form__frame_section__text_color_label',
						)}
					/>
					<Checkbox
						disabled={disableFrameBackground}
						checked={isBackgroundTransparent}
						onCheckedChange={(checked) => {
							if (checked) {
								setSavedBackgroundColor(
									backgroundColor ||
										DEFAULT_FRAME_BACKGROUND_COLOR,
								)
							}
							setBackgroundColor(
								checked
									? TRANSPARENT_COLOR
									: savedBackgroundColor ||
											DEFAULT_FRAME_BACKGROUND_COLOR,
							)
						}}
						id="frame-transparency"
						label={t(
							'generator__design_form__pattern_section__transparent_background',
						)}
						className="mt-4 tablet:mt-6"
					/>
				</div>
			) : null}
		</div>
	)
}

export const useFrameDesignForm = (): TGeneratorFormSection => {
	const t = useTranslations()

	return {
		id: 'frame',
		title: t('generator__design_form__frame_section__title'),
		description: t(
			'generator__design_form__frame_section__description__changed',
		),
		// TODO: Temporary key. After deleting the old `...__description` from Lokalise, rename this __changed key back to `...__description`.
		icon: <LineFrame />,
		Content: FrameDesignForm,
	}
}
